import {CommentsCategory} from '@hconnect/common/types'
import React from 'react'
import {Controller, useFormContext} from 'react-hook-form'

import {useAdminPilotUser} from '../../../common/hooks/useAdminPilotUser'
import {useCustomerByNameFeature} from '../../../hooks/useFeatures'
import {useTranslation} from '../../../hooks/useTranslation'
import {LookupDataScopeInput} from '../../../modules/DataScopeConfiguration'
import {
  ADD_CUSTOMER,
  ADD_CUSTOMER_SCOPE,
  ADD_HAULIER,
  ADD_PAYER,
  ADD_PLANT,
  GroupedRoleReducerAction
} from '../../../modules/GroupedRoleAssignmentReducer'
import {Permission} from '../../../modules/LoggedInUser'
import {GroupedRoleAssignment, isRoleTypeUngroupable} from '../../../modules/ManageUsers.selectors'
import {RoleCommentCategoryLookupSelect} from '../../RoleCommentCategoryLookupSelect'
import {RoleContractLookupTextField} from '../../RoleContractLookupTextField'
import {RoleCustomerLookupDropdownTextField} from '../../RoleCustomerLookupDropdownTextField'
import {RoleCustomerLookupTextField} from '../../RoleCustomerLookupTextField'
import {RoleHaulierLookupTextField} from '../../RoleHaulierLookupTextField'
import {RolePayerLookupTextField} from '../../RolePayerLookupTextField'
import {RolePlantLookupTextField} from '../../RolePlantLookupTextField'
import {checkRequired} from '../utils'

type LookupDataScopeProps = {
  isRequired: boolean
  errorMessage: string
  configuration: LookupDataScopeInput
  groupedRoleAssignment: GroupedRoleAssignment
  dataScopeName: string
  dispatchRoleStateUpdate: React.Dispatch<GroupedRoleReducerAction>
  multiSelect: boolean
  setShowCustomerOrPayerDropdown: (show: boolean) => void
  showCustomerOrPayerDropdown: boolean
  customerAdminData?: Record<string, string | undefined>
  loggedInUserPermissions?: Permission[] | null
  roleDataScopeNames?: Set<string>
}

const CommentsCategoryTranslationKeys: Record<CommentsCategory, string> = {
  [CommentsCategory.Assets]: 'assets',
  [CommentsCategory.AssetOperationTimes]: 'assetoperationtimes',
  [CommentsCategory.POMQuestions]: 'POMquestions',
  [CommentsCategory.MaterialOrders]: 'materialorders',
  [CommentsCategory.HrocRegionalManagerKpis]: 'hrocRegionalManagerKpis',
  [CommentsCategory.ProcessWorkOrders]: 'processWorkOrders'
}

export const LookupDataScope = ({
  isRequired,
  errorMessage,
  configuration,
  groupedRoleAssignment,
  dataScopeName,
  dispatchRoleStateUpdate,
  multiSelect,
  customerAdminData,
  setShowCustomerOrPayerDropdown,
  showCustomerOrPayerDropdown,
  loggedInUserPermissions,
  roleDataScopeNames
}: LookupDataScopeProps) => {
  const {control, getValues} = useFormContext<GroupedRoleAssignment>()
  const {t} = useTranslation()
  const fieldName = `commonDataScopes.${dataScopeName}`
  const isUserAdminPilot = useAdminPilotUser(loggedInUserPermissions)
  const {isCustomersByNameSearch, isLoading} = useCustomerByNameFeature()

  return (
    <Controller
      key={dataScopeName}
      name={fieldName as keyof GroupedRoleAssignment}
      control={control}
      rules={{
        validate: (value: any) => {
          if (groupedRoleAssignment.variantDataScopes.customersScopes.length > 0) {
            // Validation fine, as LOOKUP_DATA_SCOPE_TYPE is only valid for customers.
            return true
          }
          if (
            dataScopeName === 'payerIds' &&
            groupedRoleAssignment.variantDataScopes.payerIds &&
            groupedRoleAssignment.variantDataScopes.payerIds.length > 0
          ) {
            // Validation fine, as LOOKUP_DATA_SCOPE_TYPE is only valid for payers.
            return true
          }
          if (
            dataScopeName === 'plantIds' &&
            groupedRoleAssignment.variantDataScopes.plantIds &&
            groupedRoleAssignment.variantDataScopes.plantIds.length > 0
          ) {
            // Validation fine, as LOOKUP_DATA_SCOPE_TYPE is only valid for plants.
            return true
          }
          if (
            dataScopeName === 'haulierIds' &&
            groupedRoleAssignment.variantDataScopes.haulierIds &&
            groupedRoleAssignment.variantDataScopes.haulierIds.length > 0
          ) {
            // Validation fine, as LOOKUP_DATA_SCOPE_TYPE is only valid for hauliers.
            return true
          }

          return (
            checkRequired(
              isRequired,
              multiSelect,
              t('roleAssignment.dataScopeRequired', {
                dataScope: t(`roleAssignment.dataScope.${dataScopeName}`)
              })
            )(value) || true
          )
        }
      }}
      // eslint-disable-next-line complexity
      render={({field}: any) => {
        const dependenciesMet =
          configuration.dependsOn !== undefined
            ? !configuration.dependsOn
                .filter((dep) => !roleDataScopeNames || roleDataScopeNames.has(dep))
                .map((dep) => getValues(`commonDataScopes.${dep}`))
                .some((dependency) => dependency === undefined)
            : true

        const customerAdminPayerDependenciesMet =
          isUserAdminPilot && groupedRoleAssignment.variantDataScopes.customersScopes.length === 0

        const customersList = []
        const payersList = []

        if (
          dataScopeName === 'payerIds' &&
          groupedRoleAssignment.variantDataScopes.customersScopes.length === 0 &&
          (groupedRoleAssignment.variantDataScopes.payerIds?.length || 0) === 0 &&
          (isUserAdminPilot
            ? showCustomerOrPayerDropdown ||
              groupedRoleAssignment.variantDataScopes.payerIds?.length === undefined ||
              groupedRoleAssignment.variantDataScopes.payerIds?.length === 0
            : true)
        ) {
          return (
            <RolePayerLookupTextField
              payers={payersList}
              groupedRoleAssignment={groupedRoleAssignment}
              onChangePayerIds={(payerIds: string[]) => {
                dispatchRoleStateUpdate({
                  type: ADD_PAYER,
                  payload: payerIds[0]
                })
              }}
              disabled={isUserAdminPilot ? !customerAdminPayerDependenciesMet : !dependenciesMet}
              name={field.name}
              helperText={errorMessage}
              dataScopeName={dataScopeName}
              customerAdminData={customerAdminData}
              setShowCustomerOrPayerDropdown={setShowCustomerOrPayerDropdown}
              loggedInUserPermissions={loggedInUserPermissions}
            />
          )
        }

        if (dataScopeName === 'plantIds' || dataScopeName === 'plantId') {
          return (
            <RolePlantLookupTextField
              groupedRoleAssignment={groupedRoleAssignment}
              dataScopeName={dataScopeName}
              disabled={!dependenciesMet}
              helperText={errorMessage}
              onChangePlantIds={(plantId: string) => {
                dispatchRoleStateUpdate({
                  type: ADD_PLANT,
                  payload: plantId
                })
              }}
            />
          )
        }

        if (dataScopeName === 'commentCategories') {
          const commentCategoriesOptions = [
            ...Object.values(CommentsCategory).map((category) => ({
              value: category.toString(),
              label: t(
                `roleAssignment.commentCategoryItems.${CommentsCategoryTranslationKeys[category]}`
              )
            })),
            {
              value: '',
              label: 'None'
            }
          ]
          return (
            <RoleCommentCategoryLookupSelect
              dataScopeName={dataScopeName}
              disabled={!dependenciesMet}
              options={commentCategoriesOptions}
              onChange={(commentCategory: string) => {
                dispatchRoleStateUpdate({
                  type: 'ADD_COMMENT_CATEGORY',
                  payload: commentCategory
                })
              }}
            />
          )
        }
        if (dataScopeName === 'contractIds') {
          return (
            <RoleContractLookupTextField
              InputLabelProps={{required: isRequired}}
              groupedRoleAssignment={groupedRoleAssignment}
              dataScopeName={dataScopeName}
              disabled={!dependenciesMet}
              helperText={errorMessage}
              onChangeContractIds={(contractId) => {
                dispatchRoleStateUpdate({
                  type: ADD_CUSTOMER_SCOPE,
                  payload: {
                    contractId
                  }
                })
              }}
            />
          )
        }

        const hauliersList = []

        if (dataScopeName === 'haulierIds') {
          return (
            <RoleHaulierLookupTextField
              hauliers={hauliersList}
              groupedRoleAssignment={groupedRoleAssignment}
              onChangeHaulierIds={(haulierIds) => {
                dispatchRoleStateUpdate({
                  type: ADD_HAULIER,
                  payload: haulierIds[0]
                })
              }}
              disabled={!dependenciesMet}
              name={field.name}
              helperText={errorMessage}
              dataScopeName={dataScopeName}
            />
          )
        }

        const isCustomerSelected =
          groupedRoleAssignment.variantDataScopes.customersScopes.length > 0

        const hide = isRoleTypeUngroupable(groupedRoleAssignment.roleType) && isCustomerSelected

        if (hide) {
          return <></>
        }
        if (isLoading) {
          return <></>
        } else if (isCustomersByNameSearch) {
          return (
            <RoleCustomerLookupDropdownTextField
              InputLabelProps={{
                required: isRequired
              }}
              customers={customersList}
              groupedRoleAssignment={groupedRoleAssignment}
              onChangeCustomerIds={(customerIds) => {
                dispatchRoleStateUpdate({
                  type: ADD_CUSTOMER,
                  payload: customerIds[0]
                })
              }}
              disabled={!dependenciesMet}
              name={field.name}
              margin="normal"
              fullWidth
              helperText={errorMessage}
              dataScopeName={dataScopeName}
            />
          )
        } else {
          return dataScopeName !== 'payerIds' &&
            groupedRoleAssignment.variantDataScopes.customersScopes.length === 0 &&
            (groupedRoleAssignment.variantDataScopes.payerIds?.length || 0) === 0 &&
            (!isUserAdminPilot ||
              showCustomerOrPayerDropdown ||
              (dataScopeName !== 'payerIds' &&
                groupedRoleAssignment.variantDataScopes.customersScopes.length === 0)) ? (
            <RoleCustomerLookupTextField
              customers={customersList}
              groupedRoleAssignment={groupedRoleAssignment}
              onChangeCustomerIds={(customerIds) => {
                dispatchRoleStateUpdate({
                  type: ADD_CUSTOMER,
                  payload: customerIds[0]
                })
              }}
              disabled={isUserAdminPilot ? !isUserAdminPilot : !dependenciesMet}
              name={field.name}
              helperText={errorMessage}
              dataScopeName={dataScopeName}
              customerAdminData={customerAdminData}
              showCustomerOrPayerDropdown={showCustomerOrPayerDropdown}
              setShowCustomerOrPayerDropdown={setShowCustomerOrPayerDropdown}
              loggedInUserPermissions={loggedInUserPermissions}
            />
          ) : (
            <></>
          )
        }
      }}
    />
  )
}
